import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);

const KEY = (slug, tableId) => `business.cart.${slug}.${tableId || "counter"}`;
const lineKeyFor = (item) => `${item.id}:${item.menuItemSizeId || "base"}`;
const normaliseLine = (item) => ({ ...item, lineId: item.lineId || lineKeyFor(item) });
export function clearVendorCarts(vendorSlug) { const prefix = `business.cart.${vendorSlug}`; Object.keys(localStorage).filter((key) => key === prefix || key.startsWith(`${prefix}.`)).forEach((key) => localStorage.removeItem(key)); }

export function CartProvider({ vendorSlug, tableId = null, taxSettings = { enabled: true, rate: 5 }, children }) {
  const [items, setItems] = useState([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (!vendorSlug) return;
    try {
      const raw = localStorage.getItem(KEY(vendorSlug, tableId)) || (!tableId ? localStorage.getItem(`business.cart.${vendorSlug}`) : null);
      setItems(raw ? JSON.parse(raw).map(normaliseLine) : []);
    } catch {
      setItems([]);
    }
    setHydrated(true);
  }, [vendorSlug, tableId]);

  useEffect(() => {
    if (!vendorSlug || !hydrated) return;
    localStorage.setItem(KEY(vendorSlug, tableId), JSON.stringify(items));
  }, [items, vendorSlug, tableId, hydrated]);

  const add = (item) => {
    setItems((prev) => {
      const lineId = lineKeyFor(item);
      const idx = prev.findIndex((p) => (p.lineId || lineKeyFor(p)) === lineId);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: next[idx].qty + 1 };
        return next;
      }
      return [...prev, {
        id: item.id,
        lineId,
        menuItemSizeId: item.menuItemSizeId || null,
        sizeLabel: item.sizeLabel || null,
        name: item.name,
        price: item.price,
        qty: 1,
        image: item.image,
      }];
    });
  };
  const remove = (lineId) => setItems((prev) => prev.filter((p) => (p.lineId || lineKeyFor(p)) !== lineId));
  const decrement = (lineId) =>
    setItems((prev) =>
      prev
        .map((p) => ((p.lineId || lineKeyFor(p)) === lineId ? { ...p, qty: p.qty - 1 } : p))
        .filter((p) => p.qty > 0)
    );
  const increment = (lineId) =>
    setItems((prev) => prev.map((p) => ((p.lineId || lineKeyFor(p)) === lineId ? { ...p, qty: p.qty + 1 } : p)));
  const clear = () => {
    // Checkout navigates away immediately after a successful order. Persist the
    // clear synchronously so an unmount cannot leave the completed basket behind.
    if (vendorSlug) {
      localStorage.removeItem(KEY(vendorSlug, tableId));
      if (!tableId) localStorage.removeItem(`business.cart.${vendorSlug}`);
    }
    setItems([]);
  };

  const subtotal = useMemo(() => items.reduce((s, i) => s + i.price * i.qty, 0), [items]);
  const count = useMemo(() => items.reduce((s, i) => s + i.qty, 0), [items]);
  const tax = taxSettings.enabled ? Math.round(subtotal * (Number(taxSettings.rate) || 0) / 100) : 0;
  const total = subtotal + tax;

  return (
    <CartContext.Provider value={{ items, add, remove, increment, decrement, clear, subtotal, tax, total, count, tableId, taxSettings }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
}
